import { ReactNode, useEffect, useMemo, useState } from 'react';

import { InspectionDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { AccessorKeyColumnDef, ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import formVector from '@/assets/vectors/form.svg';
import UndoIcon from '@/components/icons/undo';
import InspectionDrawer from '@/components/inspections/inspection-drawer';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useModulesStore } from '@/stores/modules.store';

export function InspectionsTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  columns,
}: {
  notFoundDisplay: ReactNode;
  data: InspectionDTO[];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  columns: AccessorKeyColumnDef<InspectionDTO>[];
}) {
  const { syncModuleColumns } = useModulesStore();

  const [submission, setSubmission] = useState<InspectionDTO | undefined>(undefined);

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.InspectionSubmissions, columns);
  }, [syncModuleColumns, columns]);

  return (
    <>
      <div className="flex-1 bg-white">
        <TablePageTable<InspectionDTO>
          data={data}
          columns={columns}
          notFoundDisplay={notFoundDisplay}
          loading={loading}
          onSortingChange={onSortingChange}
          initialSorting={initialSorting}
          grouping={grouping}
          columnOrder={(columnOrder || []).map((column) => column.id)}
          onRowClick={(row) => setSubmission(row)}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          columnVisibility={columnVisibility}
          columnFilters={columnFilters}
          onColumnFiltersChange={onColumnFiltersChange}
        />
      </div>

      <InspectionDrawer open={!!submission} onOpenChange={() => setSubmission(undefined)} inspectionId={submission?.id} noTrigger />
    </>
  );
}

export function NoInspectionsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={formVector} alt="Form" className="h-28" />
      <span className="text-sm">No inspections match your search</span>
    </div>
  );
}

export function NoInspectionsFoundFromFiltersDisplay({ removeColumnFilters }: { removeColumnFilters: () => void }) {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={formVector} alt="Form" className="h-28" />
      <span className="text-xl font-medium">No inspections match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
}

export function NoInspectionsFound() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4 text-center">
      <img src={formVector} alt="Form" className="h-28" />
      <span className="text-xl font-[500]">No records have been created yet</span>
      <span className="text-secondary-text max-w-[420px] text-center text-sm">
        When your workers submit records using this form they will appear here. To get started, send an access link to users.
      </span>
    </div>
  );
}
